import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { type Answers, useFormProvider } from '@/providers/FormProvider';
import { showSuccessToaster } from '@/utils/toasters';
import FormSearchDialog from '@/dialogs/FormSearchDialog';
import Overlay from '@/components/Overlay';
import ContinueWarningDialog from '@/dialogs/ContinueWarningDialog';
import FormBuilder from './FormBuilder';
import FormHeader from './FormHeader';
import FieldsList from './FieldsList';
import { classes } from './utils';

export type FormBuilderOverlayProps = {
  submissionId?: number;
  onSave: (data: {
    formId: number;
    versionId: number;
    answers: Answers;
  }) => Promise<void>;
  onClose: () => void;
};

export default function FormBuilderOverlay({
  submissionId,
  onSave,
  onClose,
}: FormBuilderOverlayProps) {
  const { onApiError } = useAppState();
  const { isChanged, formId, versionId, answers, onSelectForm } =
    useFormProvider();
  const [isSaving, toggleSaving] = useToggle();
  const [isCloseWarningOpen, toggleCloseWarning] = useToggle();

  const handleSave = async () => {
    try {
      toggleSaving(true);
      await onSave({ formId: formId!, versionId: versionId!, answers });
      toggleSaving(false);
      showSuccessToaster('Form saved successfully');
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving form', () => handleSave());
    }
  };

  const handleStartClose = () => {
    return isChanged ? toggleCloseWarning(true) : onClose();
  };

  const handleCloseSearch = () => {
    // Search will be hidden if a form is selected. Closing it without selecting a form should close the whole overlay.
    if (!formId) onClose();
  };

  if (!(formId || submissionId)) {
    return (
      <FormSearchDialog onSelect={onSelectForm} onClose={handleCloseSearch} />
    );
  }

  if (!versionId) return null; // TODO

  return (
    <>
      {isCloseWarningOpen && (
        <ContinueWarningDialog
          onContinue={onClose}
          onCancel={() => toggleCloseWarning(false)}
        />
      )}
      <Overlay className={classes.overlay} onClose={handleStartClose}>
        <FormHeader onSave={handleSave} onClose={handleStartClose} />
        <div className={classes.content}>
          <FieldsList />
          <FormBuilder />
        </div>
      </Overlay>
    </>
  );
}
